import React, { useCallback, useEffect } from 'react';
import { Box } from 'folds';
import { useParams } from 'react-router-dom';
import { isKeyHotkey } from 'is-hotkey';
import { RoomView } from './RoomView';
import { ScreenSize, useScreenSizeContext } from '../../hooks/useScreenSize';
import { PowerLevelsContextProvider, usePowerLevels } from '../../hooks/usePowerLevels';
import { useRoom } from '../../hooks/useRoom';
import { useKeyDown } from '../../hooks/useKeyDown';
import { markAsRead } from '../../../client/action/notifications';
import { useMatrixClient } from '../../hooks/useMatrixClient';
import { useDocArea } from '../../contexts/DocAreaContext';
import { useRoomMembersCtx } from '../../contexts/RoomMembersContext';
import { useRoomRoleAccessing } from '../../state/room/roomRoleAccessing';
import { inviteDefaultContacts } from '../../../client/action/room';

type RoomProps = {
  setHasRoomSelected: React.Dispatch<React.SetStateAction<boolean>>,
  setRoomClosed: React.Dispatch<React.SetStateAction<boolean>>,
 };
export function Room({setHasRoomSelected, setRoomClosed}: RoomProps) {
  const { eventId } = useParams();
  const room = useRoom();
  const mx = useMatrixClient();

  const screenSize = useScreenSizeContext();
  const powerLevels = usePowerLevels(room);

  const { setActiveRoom, clearActiveRoom } = useRoomMembersCtx();
  const { docInfo, setDocInfo, setShowDocArea, mid, setMid, setDocopen, setPressed, closeDocArea } = useDocArea();

  const { currentUserRoles } = useRoomRoleAccessing(mx);

  useKeyDown(
    window,
    useCallback(
      (evt) => {
        if (isKeyHotkey('escape', evt)) {
          markAsRead(mx, room.roomId);
        }
      },
      [mx, room.roomId]
    )
  );

  useEffect(() => {
    if (screenSize === ScreenSize.Desktop) {
      setActiveRoom(room);
    }
    return () => clearActiveRoom();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room.roomId]);

  useEffect(() => {
    closeDocArea();
    return () => closeDocArea();
  }, [room.roomId]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (docInfo?.hasOwnProperty('RepairInfo'))
      setDocopen(true);
    else {
      setPressed(false);
      setDocopen(false);
    }
  }, [docInfo, setDocopen, setPressed]);

  useEffect(() => {
    if (currentUserRoles.includes('User')) {
      const joinedMembers = room.getJoinedMembers();
      if (joinedMembers.length === 1 && joinedMembers[0].userId === mx.getUserId()) {
        inviteDefaultContacts(mx, room.roomId).catch((e) => {
          console.error(`inviteDefaultContacts failed for room ${room.roomId}:`, e);
        });
      }
    }
  }, [room.roomId, currentUserRoles, mx]);

  return (
    <PowerLevelsContextProvider value={powerLevels}>
      <Box grow="Yes">
        <RoomView setHasRoomSelected={setHasRoomSelected} setRoomClosed={setRoomClosed} setShowDocArea={setShowDocArea} mid={mid} setMid={setMid} setDocInfo={setDocInfo} room={room} eventId={eventId} />
      </Box>
    </PowerLevelsContextProvider>
  );
}
